// ============================================================
// TMR Portal Admin — o contrato como o tmr_v2 o conhece
// ============================================================
import { useEffect, useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@tmr/api-client';
import { Btn, StatusBadge } from '../../components/primitives';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Drawer, Field } from '../../components/ui';
import { Notice } from '../../components/Notice';
import { Skeleton, ErrorState } from '../../components/states';
import { Icons, StrokeIcon } from '../../lib/icons';
import { contractKeys } from '../../data/queryClient';
import {
  useCancelContract,
  useContract,
  useContractDocument,
  useResendSignature,
} from '../../data/contracts/queries';
import type { ContractDocument, ContractRecord } from '../../data/contracts/contract';
import {
  CONTRACT_STATUS_BADGE,
  SIGNATURE_STAGE_LABEL,
  canCancel,
  canRequestSignature,
  contractFileUrl,
  documentEventLabel,
  isOutForSignature,
  signatureProviderLabel,
  signatureStage,
} from '../../data/contracts/status';
import { ChangeSignatoryModal } from './ChangeSignatoryModal';
import { SendContractModal } from './SendContractModal';
import { SigningLinkDialog } from './SigningLinkDialog';

/** Props for {@link ContractDetailPanel}. */
interface ContractDetailPanelProps {
  /** The contract to show, or null to keep the drawer closed. */
  contractId: string | null;
  onClose: () => void;
  /** Whether the operator may send for signature (`contracts.manage`). */
  canManage: boolean;
}

/**
 * Everything the portal knows about one contract — which is less than the mockup used to imply.
 *
 * There is no document here, no clause list, no signatory roster and no signature timeline, because tmr_v2
 * stores none of them: the document is a template hosted at the provider (03 §6), and no endpoint serves the
 * signed PDF (09, lacuna L4). What it shows instead is what the record actually carries — the lifecycle
 * status, which side still owes a signature, the document's key at the provider, and the id that links the
 * contract to the legacy producer.
 *
 * The panel refetches on demand rather than polling: a contract can sit awaiting signature for as long as the
 * provider's deadline allows, and its closing arrives by webhook — or, failing that, by the document read
 * below, which brings the contract up to date with what the provider recorded.
 */
export function ContractDetailPanel({ contractId, onClose, canManage }: ContractDetailPanelProps) {
  const queryClient = useQueryClient();
  const query = useContract(contractId ?? undefined);
  const cancelContract = useCancelContract();
  const resendSignature = useResendSignature();
  const [sending, setSending] = useState(false);
  const [confirmingResend, setConfirmingResend] = useState(false);
  const [changingSignatory, setChangingSignatory] = useState(false);
  const [showingLink, setShowingLink] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const contract = query.data;
  // A contract that was never sent has no document. Asking anyway would turn an ordinary state into a 404
  // on screen.
  const documentQuery = useContractDocument(contract?.id, Boolean(contract?.signatureExternalId));

  const stage = contract ? signatureStage(contract) : null;
  const cancelError = cancelContract.error instanceof ApiError ? cancelContract.error : null;
  // The drawer is reused from one contract to the next, so the outcome of a resend is only shown on the
  // contract it was for.
  const resentHere = resendSignature.variables?.contractId === contractId;
  const resendError =
    resentHere && resendSignature.error instanceof ApiError ? resendSignature.error : null;

  // Reading the document can move the contract — a signature the provider recorded and our webhook never
  // received. The fields above come from a different request, so without this they would go on saying
  // "ainda não" beside the timeline that just corrected them.
  // Invalidating the whole family, as sending does: the row behind the drawer shows the same stage, and it
  // would otherwise still read "aguardando a assinatura da empresa" once the drawer closes. It cannot loop —
  // reconciling is idempotent, so the refetch this triggers answers `reconciled: false`.
  const reconciled = documentQuery.data?.reconciled ?? false;
  useEffect(() => {
    if (!reconciled) return;
    void queryClient.invalidateQueries({ queryKey: contractKeys.all });
  }, [reconciled, queryClient]);

  const refreshAll = () => {
    void query.refetch();
    // The history lives in its own query. Refetching only the contract is what left the timeline frozen for
    // as long as the drawer stayed open.
    if (documentQuery.isFetched || documentQuery.isFetching) void documentQuery.refetch();
  };

  return (
    <Drawer
      open={Boolean(contractId)}
      onClose={onClose}
      title="Contrato"
      footer={
        contract && canManage && isOutForSignature(contract.status) ? (
          // What can be done to a document already in someone's hands — the same slot that holds "Enviar"
          // and "Cancelar" for a Pending contract, so each status shows its own set of actions.
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Btn
              variant="primary"
              icon={resendSignature.isPending ? undefined : Icons.send}
              onClick={resendSignature.isPending ? undefined : () => setConfirmingResend(true)}
              disabled={resendSignature.isPending}
            >
              {resendSignature.isPending ? 'Reenviando…' : 'Reenviar'}
            </Btn>
            <Btn variant="secondary" icon={Icons.pen} onClick={() => setChangingSignatory(true)}>
              Trocar contato
            </Btn>
            <Btn variant="ghost" icon={Icons.copy} onClick={() => setShowingLink(true)}>
              Ver link
            </Btn>
          </div>
        ) : contract && canManage && canRequestSignature(contract.status) ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Btn variant="primary" icon={Icons.send} onClick={() => setSending(true)}>
              Enviar para assinatura
            </Btn>
            {/* Cancelling sits beside sending because they are the two ways out of a Pending contract, and
                until now only one of them existed. `Btn` has no destructive variant; the ConfirmDialog it
                opens is where the weight belongs. */}
            {canCancel(contract.status) && (
              <Btn
                variant="ghost"
                onClick={cancelContract.isPending ? undefined : () => setConfirmingCancel(true)}
                disabled={cancelContract.isPending}
              >
                {cancelContract.isPending ? 'Cancelando…' : 'Cancelar contrato'}
              </Btn>
            )}
          </div>
        ) : undefined
      }
    >
      {query.isPending && <Skeleton rows={6} />}

      {query.isError && (
        <ErrorState
          error={query.error}
          onRetry={query.error instanceof ApiError && query.error.isNotFound ? undefined : () => void query.refetch()}
        />
      )}

      {contract && (
        <>
          <Field label="Situação">
            <StatusBadge status={CONTRACT_STATUS_BADGE[contract.status]} />
          </Field>
          {stage && <Field label="Assinatura">{SIGNATURE_STAGE_LABEL[stage]}</Field>}
          <Field label="Provedor">{signatureProviderLabel(contract.signatureProvider)}</Field>
          <Field label="Criado em">{formatMoment(contract.createdAt)}</Field>
          <Field label="Enviado em">{formatMoment(contract.signatureRequestedAt)}</Field>
          {/* Only for a contract that recorded one. Null means either "não enviado" — already answered by
              the line above — or a contract raised before the choice existed, and neither is "por e-mail". */}
          {contract.signatureDeliveryChannel && (
            <Field label="Enviado por">
              {contract.signatureDeliveryChannel === 'WhatsApp' ? 'WhatsApp' : 'E-mail'}
              {contract.signatureDeliveryTarget ? ` · ${contract.signatureDeliveryTarget}` : ''}
            </Field>
          )}
          <Field label="Empresa assinou">{formatMoment(contract.partySignedAt)}</Field>
          <Field label="TMR assinou">{formatMoment(contract.tmrSignedAt)}</Field>
          <Field label="Documento no provedor">
            {contract.signatureExternalId ? (
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>{contract.signatureExternalId}</span>
            ) : (
              <Absent>não enviado</Absent>
            )}
          </Field>
          <Field label="Produtor no legado">
            {contract.legacyPartyId ?? <Absent>sem vínculo</Absent>}
          </Field>
          {/* Only for a cancellation decided here. One cancelled at the provider leaves this null, and that
              absence is the only thing that tells the two apart — the status is the same word for both. */}
          {contract.cancelledAt && (
            <Field label="Cancelado em">
              {formatMoment(contract.cancelledAt)}
              {contract.cancellationReason ? ` · ${contract.cancellationReason}` : ''}
            </Field>
          )}

          {contract.legacyPartyId === null && (
            <p style={noteStyle}>
              Sem vínculo com o produtor no sistema legado — é ele que libera o repasse na assinatura. É
              resolvido sozinho quando a empresa terminar de replicar.
            </p>
          )}

          {resentHere && resendSignature.isSuccess && (
            <Notice tone="success">
              Solicitação reenviada{' '}
              {resendSignature.data.signatureDeliveryChannel === 'WhatsApp' ? 'por WhatsApp' : 'por e-mail'}
              {resendSignature.data.signatureDeliveryTarget
                ? ` para ${resendSignature.data.signatureDeliveryTarget}`
                : ''}
              .
            </Notice>
          )}

          {resendError && (
            <Notice tone="danger">
              {resendError.code === 'conflict'
                ? 'Este documento não aceita mais reenvio.'
                : 'O provedor de assinatura recusou o reenvio — nada mudou. Tente de novo.'}{' '}
              {resendError.detail}
            </Notice>
          )}

          {cancelError && (
            <Notice tone="danger">
              {cancelError.code === 'conflict'
                ? 'Este contrato não pode mais ser cancelado por aqui.'
                : 'Não foi possível cancelar o contrato — nada mudou.'}{' '}
              {cancelError.detail}
            </Notice>
          )}

          <ContractDocumentSection contract={contract} query={documentQuery} />

          <p style={{ ...noteStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>O documento e o histórico são lidos do provedor a cada abertura.</span>
            <Btn variant="ghost" size="sm" onClick={refreshAll}>
              Atualizar
            </Btn>
          </p>

          {sending && (
            <SendContractModal contractId={contract.id} onClose={() => setSending(false)} />
          )}

          {changingSignatory && (
            <ChangeSignatoryModal contract={contract} onClose={() => setChangingSignatory(false)} />
          )}

          {showingLink && (
            <SigningLinkDialog contractId={contract.id} onClose={() => setShowingLink(false)} />
          )}

          {confirmingResend && (
            <ConfirmDialog
              title="Reenviar a solicitação de assinatura?"
              confirmLabel="Reenviar"
              cancelLabel="Voltar"
              onConfirm={() => resendSignature.mutate({ contractId: contract.id })}
              onClose={() => setConfirmingResend(false)}
              message={
                <p style={{ margin: 0 }}>
                  O representante recebe de novo o <strong>mesmo documento</strong>
                  {contract.signatureDeliveryTarget ? (
                    <>
                      {' '}
                      em <strong>{contract.signatureDeliveryTarget}</strong>
                    </>
                  ) : null}
                  . Se esse contato estiver errado, use <strong>Trocar contato</strong> em vez de reenviar.
                </p>
              }
            />
          )}

          {confirmingCancel && (
            <ConfirmDialog
              title="Cancelar este contrato?"
              danger
              confirmLabel="Cancelar contrato"
              cancelLabel="Voltar"
              // The dialog closes itself as soon as onConfirm runs, so the mutation's outcome is reported by
              // the panel behind it — not in here, where nothing would be left to show it.
              onConfirm={() => {
                cancelContract.mutate({
                  contractId: contract.id,
                  reason: cancelReason.trim() || null,
                });
                setCancelReason('');
              }}
              onClose={() => setConfirmingCancel(false)}
              message={
                <>
                  <p style={{ margin: '0 0 12px' }}>
                    O contrato é encerrado como <strong>cancelado</strong> e sai da fila de pendentes de
                    envio. Nada é enviado a ninguém, porque ele nunca foi ao provedor.{' '}
                    <strong>Para corrigir dados da empresa não é preciso cancelar</strong> — o documento é
                    gerado no envio, então basta editar o cadastro e enviar depois.
                  </p>
                  <label style={{ display: 'block', fontSize: 12.5, marginBottom: 5 }}>
                    Motivo (opcional)
                    <input
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      maxLength={500}
                      placeholder="Ex.: contrato duplicado"
                      style={reasonInputStyle}
                    />
                  </label>
                </>
              }
            />
          )}
        </>
      )}
    </Drawer>
  );
}

/**
 * The document at the provider: where to download it, and everything that has happened to it.
 *
 * Read live and never cached, because the download links expire within minutes. A failure here shows in
 * place and nothing else on the panel is disturbed — the status and timestamps above come from our own
 * database and remain true whether or not the provider answers.
 *
 * The query is owned by the panel rather than by this section, so "Atualizar" can refresh both halves. It
 * used to live here, which is why that button refreshed the contract and left the history frozen.
 */
function ContractDocumentSection({
  contract,
  query,
}: {
  contract: ContractRecord;
  query: UseQueryResult<ContractDocument>;
}) {
  if (!contract.signatureExternalId) return null;

  return (
    <section style={{ marginTop: 22 }}>
      <h3 style={sectionTitleStyle}>Documento</h3>

      {query.isPending && <Skeleton rows={3} />}

      {query.isError && (
        <p style={{ ...noteStyle, margin: 0 }} role="alert">
          Não foi possível ler o documento no provedor agora.{' '}
          {query.error instanceof ApiError ? query.error.detail : null}{' '}
          <button type="button" onClick={() => void query.refetch()} style={inlineButtonStyle}>
            Tentar de novo
          </button>
        </p>
      )}

      {query.data && (
        <>
          {/* Downloads go through our own API, not the provider's link. Same-origin means `download` is
              honoured and the response is an attachment, so the browser saves the file instead of handing a
              .docx to the Office online viewer — which is what produced a 404 from Microsoft's servers. */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
            {query.data.hasSignedFile && (
              <a href={contractFileUrl(contract.id, 'signed')} download style={downloadStyle}>
                <StrokeIcon d={Icons.download} size={15} /> Baixar assinado
              </a>
            )}
            {query.data.hasOriginalFile && (
              <a href={contractFileUrl(contract.id, 'original')} download style={downloadStyle}>
                <StrokeIcon d={Icons.download} size={15} /> Baixar original
              </a>
            )}
          </div>

          {/* The provider's own status word, shown only when it disagrees with ours. Seeing the two differ
              is often exactly why somebody opened this panel — a callback still in flight, or one lost. */}
          {query.data.status && (
            <Field label="Situação no provedor">
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>{query.data.status}</span>
            </Field>
          )}
          {query.data.deadlineAt && (
            <Field label="Prazo para assinar">{formatMoment(query.data.deadlineAt)}</Field>
          )}

          <h3 style={{ ...sectionTitleStyle, marginTop: 20 }}>Histórico</h3>
          {query.data.events.length === 0 ? (
            <p style={{ ...noteStyle, margin: 0 }}>O provedor não registrou nenhum evento ainda.</p>
          ) : (
            <ol style={timelineStyle}>
              {query.data.events.map((event, index) => (
                <li key={`${event.name}-${event.occurredAt}-${index}`} style={timelineItemStyle}>
                  <div style={{ fontSize: 13.5, color: 'var(--ink-900)' }}>
                    {documentEventLabel(event.name, event.actorRole)}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--fg-subtle)', marginTop: 2 }}>
                    {formatMoment(event.occurredAt)}
                    {event.actorName ? ` · ${event.actorName}` : ''}
                    {event.actorEmail ? ` · ${event.actorEmail}` : ''}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </>
      )}
    </section>
  );
}

/** A value the record does not carry — worded as absent rather than shown as an empty cell. */
function Absent({ children }: { children: string }) {
  return <span style={{ color: 'var(--fg-subtle)', fontWeight: 400 }}>{children}</span>;
}

/** A timestamp as the operator reads it, or an explicit "ainda não". */
function formatMoment(value: string | null): React.ReactNode {
  if (!value) return <Absent>ainda não</Absent>;
  return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

const noteStyle = {
  margin: '14px 0 0',
  fontFamily: 'var(--font-sans)',
  fontSize: 12.5,
  lineHeight: 1.55,
  color: 'var(--fg-subtle)',
  gap: 12,
};

const sectionTitleStyle = {
  fontFamily: 'var(--font-sans)',
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
  color: 'var(--fg-subtle)',
  margin: '0 0 10px',
};

const downloadStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--forest-800)',
  textDecoration: 'none',
  padding: '7px 12px',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--sage-100)',
};

const inlineButtonStyle = {
  background: 'transparent',
  border: 0,
  padding: 0,
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
  fontSize: 12.5,
  color: 'var(--forest-800)',
  textDecoration: 'underline',
};

const reasonInputStyle = {
  display: 'block',
  width: '100%',
  marginTop: 4,
  padding: '8px 10px',
  fontFamily: 'var(--font-sans)',
  fontSize: 13,
  color: 'var(--ink-900)',
  background: 'var(--cream-50)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
};

const timelineStyle = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  borderLeft: '2px solid var(--border-default)',
};

const timelineItemStyle = { padding: '0 0 14px 14px', position: 'relative' as const };
